import { createContext, ReactNode, useContext, useState } from 'react';
import { Dialog, DialogProps } from '../components/Dialog';

interface IDialog extends Omit<DialogProps, 'isOpen'> {
  id: string;
}

interface SetActionButtonProps {
  id: string;
  primaryButton?: ReactNode;
  cancelButton?: ReactNode;
}

interface DialogContext {
  openDialog: (dialog: IDialog) => void;
  closeDialog: (id: string) => void;
  setContent: (id: string, content: ReactNode) => void;
  setTitle?: (id: string, title: string | JSX.Element | undefined) => void;
  setActionButtons: (props: SetActionButtonProps) => void;
}

const DialogContext = createContext<DialogContext>({
  openDialog: () => null,
  closeDialog: () => null,
  setContent: () => null,
  setActionButtons: () => null,
  setTitle: () => null,
});

interface DialogProviderProps {
  children: ReactNode;
}

export function DialogProvider({ children }: DialogProviderProps) {
  const [dialogs, setDialogs] = useState<IDialog[]>([]);

  const openDialog = (dialog: IDialog) => {
    const existingDialog = dialogs.find((d) => dialog.id === d.id);
    if (existingDialog) {
      return;
    }
    setDialogs((dialogs) => [...dialogs, dialog]);
  };

  const closeDialog = (id: string) => {
    setDialogs((dialogs) => dialogs.filter((dialog) => dialog.id !== id));
  };

  const setTitle = (id: string, title: string | JSX.Element | undefined) => {
    setDialogs((dialogs) =>
      dialogs.map((dialog) =>
        dialog.id === id ? { ...dialog, title } : dialog,
      ),
    );
  };

  const setContent = (id: string, content: ReactNode) => {
    const dialog = dialogs.find((dialog) => dialog.id === id);
    if (dialog) {
      const newDialog: IDialog = { ...dialog, content };

      setDialogs((dialogs) => [
        ...dialogs.filter((dialog) => dialog.id !== id),
        newDialog,
      ]);
    }
  };

  const setActionButtons = (props: SetActionButtonProps) => {
    const { id, primaryButton, cancelButton } = props;
    const dialog = dialogs.find((dialog) => dialog.id === id);

    if (dialog) {
      const newDialog: IDialog = {
        ...dialog,
        primaryButton,
        cancelButton,
      };

      setDialogs((dialogs) => [
        ...dialogs.filter((dialog) => dialog.id !== id),
        newDialog,
      ]);
    }
  };

  return (
    <DialogContext.Provider
      value={{
        openDialog,
        closeDialog,
        setContent,
        setActionButtons,
        setTitle,
      }}
    >
      {children}
      {dialogs.map(({ id, handleCloseModal, ...rest }) => {
        return (
          <Dialog
            isOpen
            key={id}
            handleCloseModal={handleCloseModal ?? (() => closeDialog(id))}
            {...rest}
          />
        );
      })}
    </DialogContext.Provider>
  );
}

export const useDialog = () => {
  return useContext(DialogContext);
};
